import React, { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/molecules/app-sidebar';
import { NavHeader } from '@/components/molecules/nav-header';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks';
import { routes } from '../routing';
import { useBusinessSetup } from '@/contexts/business-setup-context';

export const AuthLayout: React.FC = () => {
  const { isAuthenticated, accessToken } = useAuth();
  const {
    hasSetupAccount,
    loading,
    lookupFailed,
    checkBusinessSetup,
    retryBusinessSetup,
  } = useBusinessSetup();
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    if (
      isAuthenticated &&
      accessToken &&
      hasSetupAccount === null &&
      !loading &&
      !lookupFailed
    ) {
      checkBusinessSetup(accessToken);
    }
  }, [
    accessToken,
    checkBusinessSetup,
    hasSetupAccount,
    isAuthenticated,
    loading,
    lookupFailed,
  ]);

  if (!isAuthenticated) return <Navigate to={routes.login} replace />;

  const isAccountSetupRoute = location.pathname === routes.accountSetup;
  if (isAccountSetupRoute) return <Outlet />;

  // Whether this user has a business is unknown until the lookup returns.
  // Rendering the app shell here used to flash a broken dashboard for a frame,
  // and treating the unknown as "no business" sent established customers into
  // the onboarding wizard whenever the API hiccuped.
  if (lookupFailed) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4 px-6 text-center bg-light-gray">
        <p className="text-base font-medium">{t('errors.loadAccountFailed')}</p>
        <p className="max-w-md text-sm text-muted-foreground">
          {t('errors.loadAccountFailedHint')}
        </p>
        <Button onClick={retryBusinessSetup}>{t('common.tryAgain')}</Button>
      </div>
    );
  }

  if (hasSetupAccount === null) {
    return (
      <div className="flex items-center justify-center h-screen bg-light-gray">
        <Spinner className="size-6" />
      </div>
    );
  }

  if (hasSetupAccount === false) {
    return <Navigate to={routes.accountSetup} replace />;
  }

  return (
    <div className="flex w-full h-screen bg-light-gray">
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <main className="flex flex-col h-full bg-primary-bg-default ">
            <NavHeader />
            <div className="flex-1 min-h-0 pb-6 overflow-auto">
              <Outlet />
            </div>
          </main>
        </SidebarInset>
      </SidebarProvider>
    </div>
  );
};
